import { Device } from '../../models/Device';
import { IPassage } from '../../models/Passage';
import { PowerMode } from '../../models/PowerMode';
import { RecordMode } from '../../models/RecordMode';
import { RecordModeIridium } from '../../models/RecordModeIridium';
import { ConnectionSchedule } from '../../models/ConnectionSchedule';
import DeviceService from '../../services/DeviceService';
import PigRunService from '../../services/PigRunService';

// ── getDevice concurrency-limited queue ─────────────────────────
const GET_DEVICE_MAX_CONCURRENT = 5;
const getDevicePendingUids: Set<number> = new Set();
const getDeviceInflightUids: Set<number> = new Set();
let getDeviceStoreContext: any = null;

function getDeviceProcessQueue() {
  while (
    getDeviceInflightUids.size < GET_DEVICE_MAX_CONCURRENT &&
    getDevicePendingUids.size > 0
  ) {
    const uid = getDevicePendingUids.values().next().value!;
    getDevicePendingUids.delete(uid);
    getDeviceInflightUids.add(uid);
    getDeviceFetch(uid).finally(() => {
      getDeviceInflightUids.delete(uid);
      getDeviceProcessQueue();
    });
  }
}

function getDeviceFetch(uid: number): Promise<void> {
  return DeviceService.getDevice(uid)
    .then((response) => {
      const d = response.data;
      const updatedDevice = new Device(
        d.uId,
        d.lat,
        d.lon,
        d.lastHeartbeat,
        d.lastPassage,
        d.lastDetectionCount,
        PowerMode.byIdx(d.lastPowerMode),
        d.lastStreamMode,
        d.lastSignalStrength,
        d.lastBattery,
        d.lastPassageId,
        d.lastFrequency0Setting,
        d.lastFrequency1Setting,
        d.lastFrequency2Setting,
        d.lastFrequency3Setting,
        d.lastFrequency4Setting,
        d.lastFrequency5Setting,
        d.lastSensitivity0,
        d.lastSensitivity1,
        d.lastSensitivity2,
        d.lastSensitivity3,
        d.lastSensitivity4,
        d.lastSensitivity5,
        d.lastEnable0,
        d.lastEnable1,
        d.lastEnable2,
        d.lastEnable3,
        d.lastEnable4,
        d.lastEnable5,
        d.isIridium
          ? RecordModeIridium.intToString(d.lastRecordMode)
          : RecordMode.intToString(d.lastRecordMode),
        ConnectionSchedule.byIdx(d.lastSchedule),
        d.lastSchedule,
        d.bluetooth,
        d.isConnected === 1 ? true : false,
        d.isIridium,
        d.psgState,
        d.psgSize,
        d.imei,
        d.isAutodownload ? true : false,
        d.lastExpectedWakeupTime,
        d.lastShutdownTime,
        d.lastGeoMode,
        d.firmwareVersion,
        d.groupId,
        d.deviceType
      );
      const ctx = getDeviceStoreContext;
      const oldDevice = ctx.state.devices.find(
        (old: Device) => old.uid === uid
      );
      if (oldDevice && oldDevice.isIridium !== d.isIridium) {
        ctx.dispatch('updateDeviceIcon', {
          uid: updatedDevice.uid,
          isConnected: updatedDevice.isConnected,
          isIridium: updatedDevice.isIridium,
        });
      }
      ctx.commit('UPDATE_DEVICE', updatedDevice);
      if (ctx.state.selectedDevice && uid === ctx.state.selectedDevice.uid) {
        ctx.commit('SET_SELECTED_DEVICE', updatedDevice);
        ctx.dispatch('updateLastPassage');
      }
    })
    .catch((error) => {
      console.error(error);
      getDeviceStoreContext.dispatch('notify', error);
    });
}
import { UpdatePassageTimeStampRequest } from '@/models/ReportManager/UpdatePassageTimeStampRequest';

export interface DeviceState {
  selectedDevice: Device | null;
  devices: Device[];
  leafletLayers: Record<string, number>;
  lastDeviceStatus: Record<number, { isConnected: boolean }>;
  lastDeviceLocation: Record<number, { lat: number; lon: number }>;
}

export default {
  state: {
    selectedDevice: null as Device | null,
    devices: [] as Device[],
    leafletLayers: {} as Record<string, number>,
    lastDeviceStatus: {} as Record<number, { isConnected: boolean }>,
    lastDeviceLocation: {} as Record<number, { lat: number; lon: number }>,
  } as DeviceState,
  mutations: {
    SET_SELECTED_DEVICE(state: DeviceState, device: Device | null) {
      state.selectedDevice = device;
    },
    SET_ALL_DEVICES(state: DeviceState, devices: Device[]) {
      state.devices = devices;
    },
    UPDATE_DEVICE(state: DeviceState, d: Device) {
      const index = state.devices.findIndex(
        (item: Device) => item.uid === d.uid
      );

      if (index !== -1) {
        state.devices[index] = d;
      } else {
        state.devices.push(d);
      }
    },
    UPDATE_STATUS(state: DeviceState, payload: any) {
      state.lastDeviceStatus[payload.uid] = {
        isConnected: payload.isConnected,
      };
      // Update selectedDevice directly so DeviceInfo stays current.
      if (state.selectedDevice && state.selectedDevice.uid === payload.uid) {
        state.selectedDevice.isConnected = payload.isConnected;
      }
      // Skip mutating state.devices[].isConnected here — the LeafletMap
      // updateStatus handler reads isConnected from the action payload,
      // and DeviceTable doesn't display connection status. Avoiding the
      // in-place mutation prevents a full QTable re-render per message.
    },
    UPDATE_LOCATION(state: DeviceState, payload: any) {
      state.lastDeviceLocation[payload.uid] = {
        lat: payload.lat,
        lon: payload.lon,
      };
      if (payload.heartbeatTimeStamp) {
        const index = state.devices.findIndex(
          (item: Device) => item.uid === payload.uid
        );
        if (index !== -1) {
          state.devices[index].lastHeartbeat = payload.heartbeatTimeStamp;
        }
        if (state.selectedDevice && state.selectedDevice.uid === payload.uid) {
          state.selectedDevice.lastHeartbeat = payload.heartbeatTimeStamp;
        }
      }
    },
    /** Process a batch of heartbeat location updates in a single reactive pass. */
    UPDATE_LOCATIONS_BATCH(state: DeviceState, batch: any[]) {
      for (const payload of batch) {
        state.lastDeviceLocation[payload.uid] = {
          lat: payload.lat,
          lon: payload.lon,
        };
        // Only update the selectedDevice's timestamp (visible in DeviceInfo).
        // We intentionally skip state.devices[].lastHeartbeat here to
        // avoid triggering a full DeviceTable re-render on every heartbeat.
        if (
          payload.heartbeatTimeStamp &&
          state.selectedDevice &&
          state.selectedDevice.uid === payload.uid
        ) {
          state.selectedDevice.lastHeartbeat = payload.heartbeatTimeStamp;
        }
      }
    },
    SET_DEVICE_RECORD_MODE(state: DeviceState, payload: any) {
      const index = state.devices.findIndex(
        (item: Device) => item.uid === payload.uid
      );
      // isAutoDownload is the correct property, this is coming from wacky payload in a signalR message
      state.devices[index].lastRecordMode = payload.isAutoDownload
        ? RecordMode.Downloading
        : RecordMode.Off;
      if (state.selectedDevice && state.selectedDevice.uid === payload.uid) {
        state.selectedDevice.lastRecordMode = payload.isAutoDownload
          ? RecordMode.Downloading
          : RecordMode.Off;
      }
    },
    SET_DEVICE_IS_IRIDIUM(state: DeviceState, payload: any) {
      const index = state.devices.findIndex(
        (item: Device) => item.uid === payload.uid
      );
      state.devices[index].isIridium = payload.isIridium;
      if (state.selectedDevice && state.selectedDevice.uid === payload.uid) {
        state.selectedDevice.isIridium = payload.isIridium;
      }
    },
    SET_DEVICE_PASSAGE_DETECTION_COUNT(
      state: DeviceState,
      detectionCount: any
    ) {
      if (state.selectedDevice) {
        state.selectedDevice.lastDetectionCount = detectionCount;
      }
    },
    SET_DEVICE_PASSAGE_TIMESTAMP(state: DeviceState, timestamp: string) {
      if (state.selectedDevice) {
        state.selectedDevice.lastPassage = timestamp;
      }
    },
    SET_DEVICE_STREAM_MODE(state: DeviceState, streamMode: number | null) {
      if (state.selectedDevice) {
        state.selectedDevice.lastStreamMode = streamMode;
      }
    },
    SET_DEVICE_CONNECTION_STATUS(state: DeviceState, isConnected: boolean) {
      if (state.selectedDevice) {
        state.selectedDevice.isConnected = isConnected;
      }
    },
    SET_LEAFLET_LAYERS(state: DeviceState, payload: any) {
      state.leafletLayers[payload.leafletId] = payload.uid;
    },
  },
  actions: {
    setIsIridium(context: any, payload: any) {
      // this updated directly from a signalR message
      const oldDevice = context.state.devices.find(
        (old: Device) => old.uid === payload.uid
      );
      if (oldDevice && oldDevice.isIridium !== payload.isIridium) {
        context.dispatch('updateDeviceIcon', {
          uid: oldDevice.uid,
          isConnected: oldDevice.isConnected,
          isIridium: payload.isIridium,
        });
      }
      context.commit('SET_DEVICE_IS_IRIDIUM', payload);
    },
    setRecordMode(context: any, payload: any) {
      // this updated directly from a signalR message
      context.commit('SET_DEVICE_RECORD_MODE', payload);
    },
    selectDevice(context: any, payload: { uid: number; zoom?: boolean }) {
      const device = context.state.devices.find(
        (d: Device) => d.uid === payload.uid
      );
      if (device) context.commit('SET_SELECTED_DEVICE', device);
      getDeviceStoreContext = context;
      getDeviceFetch(payload.uid);
      // EN-1498: Stream Mode in the details card / map popup reads the
      // active stream sessions. Refresh them when an AGM is opened, in case
      // a ReceiveStreamSessionUpdate was missed (e.g. during a reconnect).
      context.dispatch('fetchActiveStreamSessions');
      if (payload.zoom) context.dispatch('zoomToSelected');
    },
    clearSelection(context: any) {
      context.commit('SET_SELECTED_DEVICE', null);
    },
    updateStatus(context: any, payload: any) {
      context.commit('UPDATE_STATUS', payload);
    },
    updateLocation(context: any, payload: any) {
      context.commit('UPDATE_LOCATION', payload);
    },
    setLeafletLayer(context: any, payload: any) {
      context.commit('SET_LEAFLET_LAYERS', payload);
    },
    getDevices(context: any) {
      DeviceService.getDevices()
        .then((response) => {
          const devices: Device[] = [];
          response.data.forEach((d: any) => {
            devices.push(
              new Device(
                d.uId,
                d.lat,
                d.lon,
                d.lastHeartbeat,
                d.lastPassage,
                d.lastDetectionCount,
                PowerMode.byIdx(d.lastPowerMode),
                d.lastStreamMode,
                d.lastSignalStrength,
                d.lastBattery,
                d.lastPassageId,
                d.lastFrequency0Setting,
                d.lastFrequency1Setting,
                d.lastFrequency2Setting,
                d.lastFrequency3Setting,
                d.lastFrequency4Setting,
                d.lastFrequency5Setting,
                d.lastSensitivity0,
                d.lastSensitivity1,
                d.lastSensitivity2,
                d.lastSensitivity3,
                d.lastSensitivity4,
                d.lastSensitivity5,
                d.lastEnable0,
                d.lastEnable1,
                d.lastEnable2,
                d.lastEnable3,
                d.lastEnable4,
                d.lastEnable5,
                d.isIridium
                  ? RecordModeIridium.intToString(d.lastRecordMode)
                  : RecordMode.intToString(d.lastRecordMode),
                ConnectionSchedule.byIdx(d.lastSchedule),
                d.lastSchedule,
                d.bluetooth,
                d.isConnected === 1 ? true : false,
                d.isIridium,
                d.psgState,
                d.psgSize,
                d.imei,
                d.isAutodownload ? true : false,
                d.lastExpectedWakeupTime,
                d.lastShutdownTime,
                d.lastGeoMode,
                d.firmwareVersion,
                d.groupId,
                d.deviceType
              )
            );
          });

          context.commit('SET_ALL_DEVICES', devices);
        })
        .catch((error) => {
          console.error(error);
          context.dispatch('notify', error);
        });
    },
    getDevice(context: any, uid: number) {
      getDeviceStoreContext = context;
      if (getDeviceInflightUids.has(uid)) return; // already fetching this uid
      getDevicePendingUids.add(uid);
      getDeviceProcessQueue();
    },
    updateLastStreamMode(context: any) {
      if (!context.state.selectedDevice) return;
      // TODO this should go away when lastStreamMode starts getting returned in devices
      DeviceService.getDeviceHistory(
        context.state.selectedDevice.uid,
        'controlResponse'
      )
        .then((response: any) => {
          if (response.data.length > 0) {
            context.commit(
              'SET_DEVICE_STREAM_MODE',
              response.data[response.data.length - 1].streamEnable
            );
          } else {
            context.commit('SET_DEVICE_STREAM_MODE', null);
          }
        })
        .catch((error: any) => {
          console.error(error);
          context.dispatch('notify', error);
        });
    },

    updatePassageTimeStamps(context: any, payload: any) {
      const passageState = context.rootState.passage;
      const promiseArray: any[] = [];
      let uid = 0;

      if (passageState.passageDialogSource === 'selected_passage') {
        passageState.selectedPassages.forEach((passage: IPassage) => {
          uid = passageState.selectedPassages[0].uid;

          promiseArray.push(
            DeviceService.updateTimeStampByPassageId(
              uid,
              passage.passageId!,
              payload.newPassageTime
            )
          );
        });
      } else {
        if (passageState.passageDialogSource === 'historic_data') {
          uid = passageState.selectedPassages[0].uid;
        }

        if (passageState.passageDialogSource === 'verify_passage') {
          uid = passageState.uidToUpdate;
        }

        passageState.passageIdsToUpdate.forEach((passageId: number) => {
          promiseArray.push(
            DeviceService.updateTimeStampByPassageId(
              uid,
              passageId,
              payload.newPassageTime
            )
          );
        });
      }

      Promise.all(promiseArray)
        .then((response) => {
          context.dispatch('notify', 'Success!');
          context.dispatch('historicDataRefresh');
          context.dispatch('getDevice', uid);
          context.commit(
            'SET_SELECTED_PASSAGE_TIMESTAMP',
            payload.newPassageTimeDisplay,
            { root: true }
          );
          context.dispatch('updatedTimeStampData', {
            uid,
            passageIds: passageState.passageIdsToUpdate,
            updatedTimeStamp: payload.newPassageTimeDisplay,
          });
        })
        .catch((error) => {
          console.error(error);
          context.dispatch('notify', error);
        });
    },

    // An action dispatched by the PassageDialog.vue component to edit a passage time stamp.
    async updatePassageTimeStampFromPassageDialogComponent(
      context: any,
      payload: any
    ) {
      const passageWaveformId: number = payload.passageWaveformId;
      const updatePassageTimeStampTargetValue: Date =
        payload.updatePassageTimeStampTargetValue;

      const updatePassageTimeStampRequest: UpdatePassageTimeStampRequest =
        new UpdatePassageTimeStampRequest(
          passageWaveformId,
          updatePassageTimeStampTargetValue
        );

      await PigRunService.updatePassageTimeStamp(updatePassageTimeStampRequest)
        .then((response) => {})
        .catch((exception) => {
          console.error(exception);
        });
    },

    updatedTimeStampData(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    zoomToSelected(_context: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    updateDeviceIcon(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    heartbeatReceived(_context: any, _payload: any) {
      // signal-only: LeafletMap + DeviceInfo subscribe via subscribeAction
    },
    heartbeatBatchReceived(_context: any, _batch: any[]) {
      // signal-only: LeafletMap + DeviceInfo subscribe via subscribeAction
    },
    showHeartbeat(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    updateDetectionCount(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    deviceInfoReceived(_context: any, _payload: any) {
      // signal-only: DeviceHistory subscribes via subscribeAction
    },
    controlSettingsReceived(_context: any, _payload: any) {
      // signal-only: DeviceHistory subscribes via subscribeAction
    },
    historicDataShow(_context: any, _payload?: any) {
      // Signal-only action. Optional payload { fromDate, toDate } is
      // consumed by DeviceHistory via subscribeAction.
    },
    historicDataRefresh(_context: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    historicDataClose(_context: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },

    // setCellularCarrierInfo action.
    // Dispatched from Hubs.ts on signalR ReceiveCellularCarrierInfo event.
    // DeviceInfo.vue subscribes to this action and captures payload locally.
    setCellularCarrierInfo(_context: any, _payload: any) {
      // signal-only: DeviceInfo subscribes via subscribeAction
    },

    // setDiskUse action.
    // Dispatched from Hubs.ts when a ReceiveDiskUse signalR event is raised.
    // DeviceInfo.vue subscribes to this action and captures payload locally.
    setDiskUse(_context: any, _diskUseMsg: any) {
      // signal-only: DeviceInfo subscribes via subscribeAction
    },
  },
  getters: {},
};
